import {
  Card,
  Field,
  Form,
  NumberInput,
  Textarea,
  Text,
  H2,
} from '@payaca/ambrosia';
import {
  EESS_ITEM_LABELS,
  type EessItemKey,
} from '@payaca/helpers/solarEessItems';
import { FC, useCallback, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useRegion } from '@/api/rest/users/useRegion';
import { AccountRegions } from '@payaca/types/accountTypes';
import useUpdateAccountSolarSettings from '@/api/mutations/account/useUpdateAccountSolarSettings';
import useGetMyAccountSolarSettings from '@/api/queries/me/useGetMyAccountSolarSettings';
import {
  type AccountSolarEessSettingsInput,
  type AccountSolarSettingsFieldsFragment,
} from '@/gql/graphql';
import SettingsCardPlaceholder from './SettingsCardPlaceholder';
import SettingsSaveButton from './SettingsSaveButton';
import { useBusinessAccountForm } from './useBusinessAccountForm';

type FormState = { solarEessSettings: AccountSolarEessSettingsInput };

type Props = { readOnly: boolean };

type TextSettingProps = {
  label: string;
  item: EessItemKey;
  value: string | null;
  maxLength: number;
  rows: number;
  placeholder?: string;
  note?: string;
  disabled: boolean;
  onChange: (value: string | null) => void;
};

const TextSetting: FC<TextSettingProps> = ({
  label,
  item,
  value,
  maxLength,
  rows,
  placeholder,
  note,
  disabled,
  onChange,
}: TextSettingProps): JSX.Element => (
  <Field.Legacy>
    <Field.Label>{label}</Field.Label>
    <Field.HelperText>
      MCS 032 §6.3.2 {item}) {EESS_ITEM_LABELS[item]}
      {note ? ` ${note}` : ''}
    </Field.HelperText>
    <Textarea
      value={value ?? ''}
      maxLength={maxLength}
      rows={rows}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(text) => onChange(text === '' ? null : text)}
    />
  </Field.Legacy>
);

const CompanySettingsSolarBattery: FC<Props> = ({
  readOnly,
}: Props): JSX.Element | null => {
  const region = useRegion();
  const { solarSettings, isError } = useGetMyAccountSolarSettings();

  if (region !== AccountRegions.UK) return null;
  if (!solarSettings) {
    return <SettingsCardPlaceholder isError={isError} />;
  }

  return (
    <SolarBatteryForm
      key={JSON.stringify(solarSettings.eess)}
      readOnly={readOnly}
      settings={solarSettings}
    />
  );
};

const SolarBatteryForm: FC<
  Props & { settings: AccountSolarSettingsFieldsFragment }
> = ({ readOnly, settings: solarSettings }) => {
  const methods = useForm();
  const { mutateAsync: updateAccountSolarSettings } =
    useUpdateAccountSolarSettings();
  const { eess } = solarSettings;
  const initialState: FormState = {
    solarEessSettings: {
      selfConsumptionAllocationPercent: eess.selfConsumptionAllocationPercent,
      thirdPartyControlTerms: eess.thirdPartyControlTerms,
      endOfLifeArrangements: eess.endOfLifeArrangements,
    },
  };
  const save = useCallback(
    async (state: FormState) => {
      await updateAccountSolarSettings({ eess: state.solarEessSettings });
    },
    [updateAccountSolarSettings]
  );
  const {
    formState,
    updateFormFields,
    saveAndFlush,
    isValid,
    isDirty,
    isSaving,
  } = useBusinessAccountForm<FormState>(initialState, {}, save);

  const settings = formState.solarEessSettings;
  const patch = (partial: AccountSolarEessSettingsInput) =>
    updateFormFields({ solarEessSettings: { ...settings, ...partial } });

  return (
    <Card sizeVariant="lg">
      <Card.Body>
        <Form
          methods={methods}
          onSubmit={() => undefined}
          className="flex flex-col gap-4"
        >
          <div>
            <H2>Battery information</H2>
            <Text>
              The answers MCS 032 and MIS 3012 require you to give a customer in
              writing before contract. They print in the battery information
              block of every UK solar estimate that quotes a battery. A text
              left blank prints as to be confirmed.
            </Text>
          </div>
          <Field.Legacy>
            <Field.Label>
              Share of the battery used for storing solar (%)
            </Field.Label>
            <Field.HelperText>
              The share of usable capacity the battery uses for storing solar
              rather than other services, such as cheap-rate charging. MCS 032
              §4.4 leaves this to you. The rest of the capacity prints as an
              additional benefit.
            </Field.HelperText>
            <NumberInput
              value={settings.selfConsumptionAllocationPercent ?? null}
              min={0}
              max={100}
              step={1}
              placeholder={String(eess.defaultSelfConsumptionAllocationPercent)}
              disabled={readOnly}
              onChange={(selfConsumptionAllocationPercent) =>
                patch({ selfConsumptionAllocationPercent })
              }
            />
          </Field.Legacy>
          <TextSetting
            label="Terms of any third-party control"
            item="q"
            value={settings.thirdPartyControlTerms ?? null}
            maxLength={2000}
            rows={3}
            disabled={readOnly}
            onChange={(thirdPartyControlTerms) =>
              patch({ thirdPartyControlTerms })
            }
          />
          <TextSetting
            label="End-of-life and recycling arrangements"
            item="p"
            value={settings.endOfLifeArrangements ?? null}
            maxLength={2000}
            rows={3}
            placeholder={eess.defaultEndOfLifeArrangements}
            note="Leave blank to print the wording shown."
            disabled={readOnly}
            onChange={(endOfLifeArrangements) =>
              patch({ endOfLifeArrangements })
            }
          />
          {!readOnly && (
            <SettingsSaveButton
              label="Save battery information"
              isDirty={isDirty}
              isSaving={isSaving}
              isValid={isValid}
              onSave={saveAndFlush}
            />
          )}
        </Form>
      </Card.Body>
    </Card>
  );
};

export default CompanySettingsSolarBattery;
